import { InfiniteData } from '@tanstack/react-query';

import { PublicationDataType, PublicationEventDataType } from './executed.publication.types';

export type PublicationQueryPage = {
    data: PublicationDataType[];
    cursor?: string;
};

export type PublicationListCache = InfiniteData<PublicationQueryPage>;

/**
 * Angular `PublicationOverviewController` prepends on `created` and merges
 * an existing row on `updated` for `publicationTaskChanged`.
 */
export const applyPublicationTaskEvent = (
    pages: PublicationQueryPage[],
    event: PublicationEventDataType | undefined,
): PublicationQueryPage[] => {
    const publication = event?.publication;
    if (!publication) {
        return pages;
    }

    if (event.typeOfChange === 'created') {
        if (pages.length === 0) {
            return [{ data: [publication], cursor: undefined }];
        }

        return pages.map((page, index) => {
            if (index !== 0) {
                return page;
            }

            return {
                ...page,
                data: [
                    publication,
                    ...page.data.filter((row) => row.id != publication.id),
                ],
            };
        });
    }

    if (event.typeOfChange === 'updated') {
        return pages.map((page) => ({
            ...page,
            data: page.data.map((row) =>
                row.id == publication.id
                    ? { ...row, ...publication }
                    : row,
            ),
        }));
    }

    return pages;
};

/**
 * Fold one realtime event into the infinite-query cache. An empty cache
 * only grows on `created`; updates wait until a page exists to merge into.
 */
export const publicationListWithEvent = (
    state: PublicationListCache | undefined,
    event: PublicationEventDataType | undefined,
): PublicationListCache | undefined => {
    if (!state) {
        const publication = event?.publication;
        if (event?.typeOfChange !== 'created' || !publication) {
            return state;
        }

        return {
            pageParams: [undefined],
            pages: [{ data: [publication], cursor: undefined }],
        };
    }

    return {
        ...state,
        pages: applyPublicationTaskEvent(state.pages, event),
    };
};

/**
 * Cursor for the following page. Stop when the page is empty or the cursor
 * did not advance — the same rule as the legacy `InfiniteScrollHelper`.
 * A cursor that never changes would otherwise keep refetching the same page.
 */
export const nextPublicationCursor = (
    lastPage: PublicationQueryPage,
    allPages: PublicationQueryPage[],
): string | undefined => {
    const cursor = lastPage.cursor;
    if (!cursor || lastPage.data.length === 0) {
        return undefined;
    }

    const previous = allPages.length >= 2 ? allPages[allPages.length - 2] : undefined;
    if (previous?.cursor === cursor) {
        return undefined;
    }

    return cursor;
};
